import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon, PencilIcon, TrashIcon, MenuIcon } from '@heroicons/react/outline';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import WideBlankModal from '../modals/WideBlankModal';
import ApiRequest from '../../api/ApiRequest';
import { API_AUTH_TYPES, API_ROUTES } from '../../api/apiConstants';
import { TAG_COLORS, getColorClasses, getHexColor, DEFAULT_TAG_COLOR } from './constants';

/** react-dnd item type identifier for tag drag-and-drop operations. */
const ITEM_TYPE = 'TAG';

/**
 * DraggableTagRow - A single tag row inside the management list that supports drag-to-reorder.
 *
 * @param {Object} props
 * @param {{ id: string, name: string, color: string }} props.tag - The tag to display.
 * @param {number} props.index - Current position of this tag in the list.
 * @param {Function} props.moveTag - Called with (fromIndex, toIndex) to reorder locally.
 * @param {Function} props.onEdit - Called with the tag when the edit button is clicked.
 * @param {Function} props.onDelete - Called with the tag when the delete button is clicked.
 * @returns {JSX.Element}
 */
const DraggableTagRow = ({ tag, index, moveTag, onEdit, onDelete }) => {
  const colors = getColorClasses(tag.color);
  const hexColor = getHexColor(tag.color);
  const isCustom = colors.isCustom;

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: { index, id: tag.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (item) => {
      if (item.index !== index) {
        moveTag(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`flex items-center gap-3 px-3 py-2 bg-white border border-gray-200 rounded-lg mb-2 ${isDragging ? 'opacity-50' : ''}`}
    >
      <MenuIcon className="w-4 h-4 text-gray-400 cursor-grab" />
      <span
        className={`w-3 h-3 rounded-full ${!isCustom ? colors.dot : ''}`}
        style={isCustom ? { backgroundColor: hexColor } : {}}
      />
      <span className="flex-1 text-sm font-medium text-gray-900">{tag.name}</span>
      <span
        className={`px-2 py-0.5 text-xs rounded-full ${!isCustom ? colors.bg : ''} ${!isCustom ? colors.text : ''}`}
        style={isCustom ? { backgroundColor: `${hexColor}20`, color: hexColor } : {}}
      >
        {isCustom ? hexColor : tag.color}
      </span>
      <button
        onClick={() => onEdit(tag)}
        className="p-1 text-gray-400 hover:text-gray-600"
      >
        <PencilIcon className="w-4 h-4" />
      </button>
      <button
        onClick={() => onDelete(tag)}
        className="p-1 text-gray-400 hover:text-red-600"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

/**
 * TagForm - Controlled form for creating or editing a tag, including predefined and custom hex color pickers.
 *
 * @param {Object} props
 * @param {{ id?: string, name?: string, color?: string } | null} props.tag - Existing tag to edit, or null for a new tag.
 * @param {Function} props.onSave - Called with `{ id?, name, color }` when the form is submitted.
 * @param {Function} props.onCancel - Called when the user clicks Cancel.
 * @param {boolean} props.isLoading - When true, disables the submit button.
 * @returns {JSX.Element}
 */
const TagForm = ({ tag, onSave, onCancel, isLoading }) => {
  const [name, setName] = useState(tag?.name || '');
  const [color, setColor] = useState(tag?.color || DEFAULT_TAG_COLOR);
  const [customHex, setCustomHex] = useState(
    tag?.color?.startsWith('#') ? tag.color : ''
  );

  /** Hex value used by the native `<input type="color">` picker; derived from the selected color. */
  const currentHex = color?.startsWith('#') ? color : getHexColor(color);

  /**
   * Validates the form and calls `onSave` with the current name and color.
   * @param {React.FormEvent} e
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ id: tag?.id, name: name.trim(), color });
  };

  /**
   * Selects a predefined color by name and clears any custom hex input.
   * @param {string} colorName
   */
  const handlePredefinedColor = (colorName) => {
    setColor(colorName);
    setCustomHex(''); // Clear custom hex when selecting predefined
  };

  /**
   * Updates the custom hex input and commits it to `color` only when the value is a valid 6-digit hex.
   * @param {string} hex
   */
  const handleCustomHexChange = (hex) => {
    setCustomHex(hex);
    // Only set as color if it's a valid hex
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      setColor(hex);
    }
  };

  /**
   * Syncs both the custom hex text field and the active color when the native color picker changes.
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleColorPickerChange = (e) => {
    const hex = e.target.value.toUpperCase();
    setCustomHex(hex);
    setColor(hex);
  };

  /** True when the selected color is a custom hex value rather than a predefined name. */
  const isCustomColor = color?.startsWith('#');

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tag Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter tag name..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Predefined Colors
        </label>
        <div className="flex flex-wrap gap-2">
          {TAG_COLORS.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => handlePredefinedColor(c.name)}
              className={`w-8 h-8 rounded-full ${c.dot} ${color === c.name ? 'ring-2 ring-offset-2 ' + c.ring : ''} hover:scale-110 transition-transform`}
              title={c.name}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Custom Color
        </label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={currentHex}
            onChange={handleColorPickerChange}
            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
          />
          <input
            type="text"
            value={customHex}
            onChange={(e) => handleCustomHexChange(e.target.value)}
            placeholder="#FF5733"
            className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
            maxLength={7}
          />
          {isCustomColor && (
            <span className="text-xs text-green-600 font-medium">Custom color selected</span>
          )}
        </div>
      </div>

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Preview
        </label>
        <div className="flex items-center gap-2">
          <span
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: currentHex }}
          />
          <span className="text-sm font-medium text-gray-900">
            {name || 'Tag Name'}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !name.trim()}
          className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
        >
          {tag?.id ? 'Update' : 'Create'} Tag
        </button>
      </div>
    </form>
  );
};

/**
 * TaskTagManagementModal - Full CRUD interface for managing a firm's task tags, including
 * create, edit, archive, and drag-to-reorder operations.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {Function} props.setOpen - Setter to open or close the modal.
 * @returns {JSX.Element}
 */
const TaskTagManagementModal = ({ open, setOpen }) => {
  const queryClient = useQueryClient();
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [editingTag, setEditingTag] = useState(null);
  const [localTags, setLocalTags] = useState([]);
  const [hasReordered, setHasReordered] = useState(false);

  /** API client scoped to the task-tags routes for advisor access. */
  const tagsApi = new ApiRequest(API_ROUTES.TASK_TAGS, API_AUTH_TYPES.ADVISOR);

  // Fetch all tags
  const { data: tagsData, isLoading } = useQuery({
    queryKey: ['task-tags'],
    queryFn: tagsApi.getFn({ endpoint: 'index' }),
    onSuccess: (data) => {
      setLocalTags(data?.tags || []);
    },
  });

  /** Sends a POST request to create a new tag and refreshes the tag list on success. */
  const { mutate: createTag, isLoading: isCreating } = useMutation({
    mutationFn: (body) => tagsApi.post({ endpoint: 'create', body }),
    onSuccess: () => {
      queryClient.invalidateQueries(['task-tags']);
      setView('list');
    },
  });

  /** Sends a PATCH request to update an existing tag's name or color. */
  const { mutate: updateTag, isLoading: isUpdating } = useMutation({
    mutationFn: ({ id, ...body }) => tagsApi.patch({ endpoint: 'update', params: [id], body }),
    onSuccess: () => {
      queryClient.invalidateQueries(['task-tags']);
      setView('list');
      setEditingTag(null);
    },
  });

  /** Archives (soft-deletes) a tag by ID, removing it from the list. */
  const { mutate: archiveTag } = useMutation({
    mutationFn: (id) => tagsApi.patch({ endpoint: 'archive', params: [id], body: {} }),
    onSuccess: () => {
      queryClient.invalidateQueries(['task-tags']);
    },
  });

  /** Persists the current `localTags` display order to the server. */
  const { mutate: reorderTags } = useMutation({
    mutationFn: (tag_orders) => tagsApi.patch({ endpoint: 'reorder', body: { tag_orders } }),
    onSuccess: () => {
      queryClient.invalidateQueries(['task-tags']);
      setHasReordered(false);
    },
  });

  /**
   * Reorders `localTags` optimistically during a drag operation.
   * @param {number} fromIndex - Source index of the dragged tag.
   * @param {number} toIndex - Destination index where the tag is dropped.
   */
  const moveTag = (fromIndex, toIndex) => {
    const updated = [...localTags];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setLocalTags(updated);
    setHasReordered(true);
  };

  /** Builds a `tag_orders` payload from the current local order and triggers the reorder mutation. */
  const handleSaveOrder = () => {
    const tag_orders = localTags.map((tag, index) => ({
      id: tag.id,
      display_order: index,
    }));
    reorderTags(tag_orders);
  };

  /**
   * Routes the form submission to either `updateTag` or `createTag` based on whether `tagData.id` is set.
   * @param {{ id?: string, name: string, color: string }} tagData
   */
  const handleSave = (tagData) => {
    if (tagData.id) {
      updateTag(tagData);
    } else {
      createTag(tagData);
    }
  };

  /**
   * Opens the tag form pre-populated with the given tag for editing.
   * @param {{ id: string, name: string, color: string }} tag
   */
  const handleEdit = (tag) => {
    setEditingTag(tag);
    setView('form');
  };

  /**
   * Prompts for confirmation then archives the given tag.
   * @param {{ id: string, name: string }} tag
   */
  const handleDelete = (tag) => {
    if (window.confirm(`Are you sure you want to delete the tag "${tag.name}"?`)) {
      archiveTag(tag.id);
    }
  };

  /** Clears any editing state and switches to the blank create form. */
  const handleAddNew = () => {
    setEditingTag(null);
    setView('form');
  };

  return (
    <WideBlankModal open={open} setOpen={setOpen} title="Manage Task Tags">
      <DndProvider backend={HTML5Backend}>
        <div className="mt-4 max-h-[60vh] overflow-y-auto">
          {view === 'list' ? (
            <>
              {/* Header with add button */}
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">
                  Drag tags to reorder. Tags are used to categorize tasks.
                </p>
                <button
                  onClick={handleAddNew}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Tag
                </button>
              </div>

              {/* Tags list */}
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading tags...</div>
              ) : localTags.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No tags yet. Click "Add Tag" to create one.
                </div>
              ) : (
                <div className="space-y-1">
                  {localTags.map((tag, index) => (
                    <DraggableTagRow
                      key={tag.id}
                      tag={tag}
                      index={index}
                      moveTag={moveTag}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}

              {/* Save order button */}
              {hasReordered && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSaveOrder}
                    className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg"
                  >
                    Save Order
                  </button>
                </div>
              )}
            </>
          ) : (
            <TagForm
              tag={editingTag}
              onSave={handleSave}
              onCancel={() => {
                setView('list');
                setEditingTag(null);
              }}
              isLoading={isCreating || isUpdating}
            />
          )}
        </div>
      </DndProvider>
    </WideBlankModal>
  );
};

export default TaskTagManagementModal;
